import React, { useMemo, useState } from 'react';

import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import { LocationLinksDocument } from '@graphql/platform';

import { darkFooterLinkStyles } from '@shared/content/dark_footer_link_styles';
import { PrivacyPreferencesFooterLink } from '@shared/content/privacy_preferences_link';
import { CustomLink } from '@shared/custom_link';

import { accountUrl, careersBaseUrl } from '@utils/environment_urls';

import facebook from '@images/home/icon-facebook.svg';
import instagram from '@images/home/icon-instagram.svg';
import linkedin from '@images/home/icon-linkedin.svg';
import xLogo from '@images/home/icon-x.svg';
import footerLogo from '@images/home/logo-clutter-endorser-footer.png';
import ssl from '@images/home/ssl-badge.svg';

import {
  buildHomeLocationColumns,
  countHomeLocationGroups,
} from './home_location_columns';
import { HomeLocationLinks } from './location_links';
import { HomeResponsiveQuoteLabel } from './quote_cta';
import {
  BodySm,
  HeadingH3,
  SectionContainer,
  secondaryOnDarkButtonStyles,
} from './styles';
import { MEDIA, TOKENS } from './tokens';
import { HomeZipBar } from './zip_bar';

const FooterShell = styled.footer`
  background: ${TOKENS.color.sectionBgDark};
  color: ${TOKENS.color.onDark};
`;

const LocationsSection = styled.section`
  scroll-margin-top: ${TOKENS.headerOffset};
  padding: ${TOKENS.space[48]} ${TOKENS.space[24]} ${TOKENS.space[24]};

  ${MEDIA.md} {
    padding-left: ${TOKENS.space[40]};
    padding-right: ${TOKENS.space[40]};
  }

  ${MEDIA.lg} {
    padding-top: 80px;
    padding-left: ${TOKENS.space[48]};
    padding-right: ${TOKENS.space[48]};
  }
`;

const LocationsInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[40]};

  ${MEDIA.lg} {
    gap: ${TOKENS.space[64]};
  }
`;

const LocationsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[16]};
`;

const LocationsHeader = styled.div`
  border-bottom: 1px solid ${TOKENS.color.neutral0_10};
  padding-bottom: ${TOKENS.space[32]};
`;

const LocationsHeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[8]};
`;

const LocationsTitle = styled.h2`
  margin: 0;
  font-family: ${TOKENS.font.sans};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${TOKENS.color.inverse};

  ${MEDIA.lg} {
    font-size: ${TOKENS.type.headingH3Size};
    line-height: ${TOKENS.type.headingH3LineHeight};
    color: ${TOKENS.color.inverse};
  }
`;

const LocationsSubhead = styled.p`
  margin: 0;
  font-family: ${TOKENS.font.sans};
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: ${TOKENS.color.onDarkMuted};

  ${MEDIA.lg} {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
  }
`;

const LocationsGrid = styled.div`
  display: none;
  width: 100%;
  justify-content: space-between;
  gap: ${TOKENS.space[32]};
  padding: 0;

  ${MEDIA.lg} {
    display: flex;
  }
`;

const LocationColumn = styled.div`
  width: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[32]};

  ${MEDIA.lg} {
    width: 220px;
  }
`;

const MobileLocations = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[32]};
  padding: 0;

  ${MEDIA.lg} {
    display: none;
  }
`;

const MobileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: ${TOKENS.space[80]};
  row-gap: ${TOKENS.space[32]};
  align-items: start;
  padding: 0 ${TOKENS.space[16]};
  width: 100%;
`;

const MobileLocationColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[8]};
`;

const CtaBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${TOKENS.space[24]};
  border-radius: ${TOKENS.radius.lg};
  background: ${TOKENS.color.inverseSurface};
  padding: ${TOKENS.space[32]} ${TOKENS.space[24]} ${TOKENS.space[16]};
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  text-align: left;

  ${MEDIA.lg} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-top: 0;
    border-radius: ${TOKENS.radius.md};
    padding: 40px 48px;
  }
`;

const CtaCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${TOKENS.space[12]};
  min-width: 0;
  padding-bottom: ${TOKENS.space[24]};
  text-align: left;

  ${MEDIA.lg} {
    gap: ${TOKENS.space[8]};
    padding-bottom: 0;
  }
`;

const CtaHeading = styled.h3`
  margin: 0;
  font-family: ${TOKENS.font.sans};
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${TOKENS.color.inverse};

  ${MEDIA.lg} {
    font-size: ${TOKENS.type.headingH3Size};
    line-height: ${TOKENS.type.headingH3LineHeight};
    color: ${TOKENS.color.inverse};
  }
`;

const CtaSubhead = styled.p`
  margin: 0;
  font-family: ${TOKENS.font.sans};
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: ${TOKENS.color.onDarkMuted};

  ${MEDIA.lg} {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
  }
`;

const CtaZipWrap = styled.div`
  width: 100%;
  min-width: 0;
  flex-shrink: 1;
  align-self: stretch;

  ${MEDIA.lg} {
    width: auto;
    flex-shrink: 0;
    align-self: auto;
  }
`;

const FooterMain = styled.div`
  padding: ${TOKENS.space[64]} ${TOKENS.space[24]} 0;

  ${MEDIA.md} {
    padding-left: ${TOKENS.space[40]};
    padding-right: ${TOKENS.space[40]};
  }

  ${MEDIA.lg} {
    padding-top: 80px;
    padding-left: ${TOKENS.space[48]};
    padding-right: ${TOKENS.space[48]};
  }
`;

const FooterTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${TOKENS.space[48]};
  padding-bottom: ${TOKENS.space[48]};
  text-align: left;

  ${MEDIA.lg} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const BrandBlock = styled.div`
  display: flex;
  max-width: 283px;
  flex-direction: column;
  gap: ${TOKENS.space[24]};
`;

const BrandLogo = styled.img`
  height: 36px;
  width: 138px;
  object-fit: contain;
`;

const SocialRow = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: ${TOKENS.space[8]};
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${TOKENS.radius.pill};
  background: ${TOKENS.color.neutral0_7};
  transition: background 0.3s ease;

  &:hover {
    background: ${TOKENS.color.neutral0_10};
  }

  &:active {
    background: ${TOKENS.color.neutral0_12};
  }

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-start;
  gap: ${TOKENS.space[32]};

  ${MEDIA.lg} {
    width: auto;
    justify-content: flex-end;
  }
`;

const LinkColumn = styled.div`
  width: 180px;
  text-align: left;
`;

const LinkList = styled.ul`
  margin: ${TOKENS.space[16]} 0 0;
  padding: 0;
  list-style: none;
`;

const FooterLink = styled(CustomLink)`
  ${darkFooterLinkStyles}
`;

const ExternalFooterLink = styled.a`
  ${darkFooterLinkStyles}
`;

const FooterBottom = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${TOKENS.space[16]};
  padding: ${TOKENS.space[32]} 0;
`;

const Copyright = styled(BodySm)`
  flex: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 1.3;
`;

const ViewAllButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 52px;
  border-radius: ${TOKENS.radius.sm};
  padding: 0 ${TOKENS.space[24]};
  font-family: ${TOKENS.font.sans};
  font-size: 16px;
  line-height: 16px;
  cursor: pointer;
  transition:
    background 0.15s ease,
    filter 0.15s ease;

  ${secondaryOnDarkButtonStyles};
`;

export const HomeSiteFooter: React.FC = () => {
  const [mobileLocationsExpanded, setMobileLocationsExpanded] = useState(false);
  const { data } = useQuery(LocationLinksDocument);
  const states = data?.states ?? [];
  const pages = data?.pages ?? [];

  const desktopColumns = useMemo(
    () => buildHomeLocationColumns(states, pages),
    [pages, states],
  );
  const hasLocations = desktopColumns.some((column) => column.length > 0);
  const mobileColumns = mobileLocationsExpanded
    ? desktopColumns
    : desktopColumns.slice(0, 2);
  const mobileCollapsedGroupCount = countHomeLocationGroups(
    desktopColumns.slice(0, 2),
  );
  const totalGroupCount = countHomeLocationGroups(desktopColumns);

  return (
    <FooterShell>
      <LocationsSection id="locations">
        <SectionContainer>
          <LocationsInner>
            <LocationsBlock>
              <LocationsHeader>
                <LocationsHeaderCopy>
                  <LocationsTitle>Find Clutter in your area</LocationsTitle>
                  <LocationsSubhead>
                    Clutter operates across the United States and Canada. Find
                    your nearest location below.
                  </LocationsSubhead>
                </LocationsHeaderCopy>
              </LocationsHeader>

              <MobileLocations>
                <MobileGrid>
                  {mobileColumns.map((column, index) => (
                    <MobileLocationColumn key={index}>
                      <HomeLocationLinks groups={column} />
                    </MobileLocationColumn>
                  ))}
                </MobileGrid>
                {hasLocations &&
                  !mobileLocationsExpanded &&
                  totalGroupCount > mobileCollapsedGroupCount && (
                    <ViewAllButton
                      type="button"
                      aria-expanded={false}
                      onClick={() => setMobileLocationsExpanded(true)}
                    >
                      View all locations
                    </ViewAllButton>
                  )}
              </MobileLocations>

              {hasLocations && (
                <LocationsGrid>
                  {desktopColumns.map((column, index) => (
                    <LocationColumn key={index}>
                      <HomeLocationLinks groups={column} />
                    </LocationColumn>
                  ))}
                </LocationsGrid>
              )}
            </LocationsBlock>

            <CtaBanner>
              <CtaCopy>
                <CtaHeading>
                  Your things deserve the Clutter by Iron Mountain standard
                </CtaHeading>
                <CtaSubhead>
                  Get a free quote for your specific area.
                </CtaSubhead>
              </CtaCopy>
              <CtaZipWrap>
                <HomeZipBar
                  id="home_footer_zip"
                  container="home_footer"
                  cta={<HomeResponsiveQuoteLabel />}
                  compact
                  variant="footer"
                  surface="dark"
                  placeholder="Enter ZIP"
                />
              </CtaZipWrap>
            </CtaBanner>
          </LocationsInner>
        </SectionContainer>
      </LocationsSection>

      <FooterMain>
        <SectionContainer>
          <FooterTop>
            <BrandBlock>
              <div>
                <BrandLogo src={footerLogo} alt="Clutter by Iron Mountain" />
                <BodySm
                  $onDark
                  $muted
                  style={{
                    display: 'block',
                    marginTop: TOKENS.space[24],
                  }}
                >
                  Full-service storage in secure warehouses backed by Iron
                  Mountain.
                </BodySm>
              </div>
              <SocialRow>
                <SocialLink
                  href="https://www.instagram.com/clutter"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <img src={instagram} alt="" />
                </SocialLink>
                <SocialLink
                  href="https://www.facebook.com/clutterstorage"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <img src={facebook} alt="" />
                </SocialLink>
                <SocialLink
                  href="https://www.linkedin.com/company/clutter"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <img src={linkedin} alt="" />
                </SocialLink>
                <SocialLink
                  href="https://x.com/clutter"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="X"
                >
                  <img src={xLogo} alt="" />
                </SocialLink>
              </SocialRow>
            </BrandBlock>

            <FooterLinks>
              <LinkColumn>
                <HeadingH3
                  $onDark
                  style={{ fontFamily: TOKENS.font.sans, fontSize: 16 }}
                >
                  Company
                </HeadingH3>
                <LinkList>
                  <li>
                    <ExternalFooterLink
                      href={careersBaseUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Careers
                    </ExternalFooterLink>
                  </li>
                  <li>
                    <FooterLink to="/partnerships">Partnerships</FooterLink>
                  </li>
                </LinkList>
              </LinkColumn>
              <LinkColumn>
                <HeadingH3
                  $onDark
                  style={{ fontFamily: TOKENS.font.sans, fontSize: 16 }}
                >
                  Support
                </HeadingH3>
                <LinkList>
                  <li>
                    <FooterLink to="/help">Help center</FooterLink>
                  </li>
                  <li>
                    <ExternalFooterLink href={accountUrl('support')}>
                      Customer support
                    </ExternalFooterLink>
                  </li>
                  <li>
                    <PrivacyPreferencesFooterLink />
                  </li>
                  <li>
                    <FooterLink to="/legal/privacy_policy">Privacy</FooterLink>
                  </li>
                </LinkList>
              </LinkColumn>
            </FooterLinks>
          </FooterTop>

          <FooterBottom>
            <Copyright $onDark $muted>
              © {new Date().getFullYear()} Clutter, Inc. All rights reserved.
              DOT Number #2719785
            </Copyright>
            <img src={ssl} alt="SSL Secure" width={52} height={26} />
          </FooterBottom>
        </SectionContainer>
      </FooterMain>
    </FooterShell>
  );
};
